import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, type AppSettings } from "@/lib/api";
import { useToast } from "@/ui/Toast";

/**
 * Los ajustes de `app_config` para las secciones de Configuración.
 *
 * `PATCH /api/admin/settings` es **parcial**: cada sección manda SÓLO sus
 * campos y lo que no viaja no se toca. No mandes el objeto completo "por si
 * acaso" — si dos secciones guardan el estado entero, la última en guardar
 * revive los valores viejos que la otra acababa de cambiar.
 */
export type CamposSettings = Partial<Omit<AppSettings, "queclink_password_guardada">> & {
  /** Vacía o ausente = conservar la que ya está guardada (nunca se devuelve). */
  queclink_password?: string;
};

/**
 * Regla de guardado de Configuración, una sola para toda la pantalla:
 *
 * - Un **switch** que enciende o apaga una automatización guarda **al instante**
 *   (`alternar`), con su propio aviso. Es reversible en un clic.
 * - Lo que se **escribe** —horas, correos, credenciales, umbrales— guarda con el
 *   **botón** de su sección (`guardar`), porque un valor a medio teclear no debe
 *   viajar.
 *
 * Antes convivían los dos modelos sin regla: el switch de Jimi guardaba solo, el
 * de Queclink esperaba un botón que estaba a ~1,900px de scroll, y la sección
 * tenía que aclarar con letras cuál era cuál.
 */
export function useAppSettings() {
  const toast = useToast();
  // useToast() devuelve un objeto NUEVO en cada render: si se mete en las deps
  // de un useCallback que a su vez observa un useEffect, el efecto se dispara
  // solo y se va en un bucle de peticiones (ya pasó en el tablero de
  // proveedor). Por eso vive en un ref y las deps quedan vacías.
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  // Para revertir el pintado optimista de un switch si el guardado falla.
  const ultimo = useRef<AppSettings | null>(null);
  ultimo.current = settings;

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setSettings(await api.get<AppSettings>("/api/admin/settings"));
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toastRef.current.error("No pude cargar la configuración", msg);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  /** Guarda `campos` y nada más. Devuelve si se logró. */
  const guardar = useCallback(
    async (campos: CamposSettings, exito = "Cambios guardados"): Promise<boolean> => {
      setGuardando(true);
      try {
        setSettings(await api.patch<AppSettings>("/api/admin/settings", campos));
        toastRef.current.success(exito);
        return true;
      } catch (err) {
        const msg = err instanceof ApiError ? err.detail : "Error";
        toastRef.current.error("No pude guardar", msg);
        return false;
      } finally {
        setGuardando(false);
      }
    },
    [],
  );

  /**
   * Enciende o apaga un switch al instante.
   *
   * Pinta el cambio antes de que responda el servidor —un switch que tarda en
   * moverse se siente roto— y lo revierte si el guardado falla.
   */
  const alternar = useCallback(
    async <K extends keyof AppSettings>(
      campo: K,
      valor: AppSettings[K],
      exito: string,
    ): Promise<void> => {
      const previo = ultimo.current;
      setSettings((s) => (s ? { ...s, [campo]: valor } : s));
      try {
        setSettings(await api.patch<AppSettings>("/api/admin/settings", { [campo]: valor }));
        toastRef.current.success(exito);
      } catch (err) {
        setSettings(previo);
        const msg = err instanceof ApiError ? err.detail : "Error";
        toastRef.current.error("No pude cambiar el ajuste", msg);
      }
    },
    [],
  );

  return { settings, cargando, guardando, guardar, alternar, recargar: cargar };
}

/**
 * Borrador local de los campos de UNA sección, con detección de cambios.
 *
 * `extraer` dice qué campos le tocan a esta sección. El borrador se resincroniza
 * cuando `settings` cambia de referencia (al cargar y al guardar) ajustando el
 * estado durante el render — el patrón de React para derivar estado, el mismo
 * que usa `lib/useCommitOnBlur`. Con `useEffect` habría un parpadeo con el valor
 * viejo, y con `key` se perdería el foco del campo que estás escribiendo.
 */
export function useBorradorSeccion<T extends CamposSettings>(
  settings: AppSettings | null,
  extraer: (s: AppSettings) => T,
) {
  const [origen, setOrigen] = useState(settings);
  const [valores, setValores] = useState<T | null>(settings ? extraer(settings) : null);

  if (origen !== settings) {
    setOrigen(settings);
    setValores(settings ? extraer(settings) : null);
  }

  const set = useCallback((cambio: Partial<T>) => {
    setValores((v) => (v ? { ...v, ...cambio } : v));
  }, []);

  const sucio =
    !!valores && !!settings && JSON.stringify(valores) !== JSON.stringify(extraer(settings));

  return { valores, set, sucio };
}
